import React, { useState, useEffect, useRef, useContext, createContext, useMemo } from 'react'
import Table from './components/Table'
import { useParams } from 'react-router-dom'
import numeral from 'numeral'
import { categories as defaultCategories } from './components/CategorySelect'
import Loading from './components/Loading'
import Button from './components/Button'
import TableToExcel from './lib/table-to-excel'
import regionStore from './stores/region'
import destinationsStore from './stores/destination'

const ReportContext = createContext()

const ExcelTable = ({ title, children }) => {
  const ref = useRef(null)
  const report = useContext(ReportContext)
  function exportTableToCsv() {
    const table = ref.current.querySelector('table').cloneNode(true)
    table.querySelectorAll('td[data-t="n"]').forEach(td => {
      td.textContent = numeral(td.textContent).value()
    })
    TableToExcel.convert(table, {
      name: report.start_date + '_' + report.end_date + '_' +title.toLowerCase().replace(/[^a-z0-9]/g, "-") + ".xlsx",
      sheet: {
        name: 'Sheet 1',
        ySplit: 1,
      }
    })
  }
  return (
    <div>
      <div className="flex justify-between gap-4 mb-4">
        <h1 className="text-4xl">{title}</h1>
        <Button onClick={exportTableToCsv}>Export to Excel</Button>
      </div>
      <div ref={ref}>
        {children}
      </div>
    </div>
  )
}

export default function () {

  const { id } = useParams()
  const [categories, setCategories] = useState(defaultCategories)
  const [loading, setLoading] = useState(true)
  const [report, setReport] = useState({})
  const [allRegions, fetchRegions, loadingRegions] = regionStore(state => [state.collection, state.fetch, state.fetching])
  const [allDestinations, fetchDestinations, loadingDestinations] = destinationsStore(state => [state.collection, state.fetch, state.fetching])

  const regions = useMemo(() => {
    if (!allRegions?.length) return []
    if (!report?.transactions?.length) return []
    const regionIds = new Set()
    for (const transaction of report.transactions) {
      if (transaction.region_id == null) continue
      regionIds.add(transaction.region_id)
    }

    return allRegions.filter(region => regionIds.has(region.record_id))
  }, [allRegions, report.transactions])

  const destinations = useMemo(() => {
    if (!allDestinations?.length) return []
    if (!report?.transactions?.length) return []
    const destinationIds = new Set()
    for (const transaction of report.transactions) {
      for (const destination of transaction.destinations_transactions_attributes) {
        if (destination.destination_id == null) continue
        destinationIds.add(destination.destination_id)
      }
    }
    return allDestinations.filter(destination => destinationIds.has(destination.record_id))
  }, [allDestinations, report.transactions])

  useEffect(() => {
    fetchRegions()
    fetchDestinations()
  }, [])

  useEffect(() => {
    fetch(`/api/reports/${id}.json`)
      .then(res => res.json())
      .then(setReport)
      .then(() => setLoading(false))
  }, [id])

  const transactionTotal = (transaction, property, swag = null, excludeReturnable = false) => {
    return transaction.items_attributes.reduce((acc, item) => {
      if (excludeReturnable && item.returnable) return acc
      const totalCost = parseInt(item.quantity ?? 0) * parseFloat(item[property] ?? 0)
      if (swag === null) return acc + totalCost
      if (swag === true && item.swag !== true) return acc
      if (swag === false && item.swag === true) return acc
      return acc + totalCost
    }, 0)
  }

  const totalPriceByFinanceCategory = (financeCategory = false, swag = null) => {
    return filteredTransactions
      .map(transaction => {
        const total = transactionTotal(transaction, 'price', swag)
        if (financeCategory) {
          const destinations = transaction.destinations.filter(d => d.financeCategory == financeCategory)
          if (destinations) {
            return total * destinations.reduce((acc, d) => parseInt(d.percentage) + acc, 0) / 100.0
          }
        }
        if (transaction.destinations.length == 0) return total
        return 0
      })
      .reduce((acc, price) => acc + price, 0)
  }

  const totalCostByFinanceCategory = (financeCategory = false, swag = null) => {
    return filteredTransactions
      .map(transaction => {
        const total = transactionTotal(transaction, 'cost', swag, true)
        if (financeCategory) {
          const destinations = transaction.destinations.filter(d => d.financeCategory == financeCategory)
          if (destinations) {
            return total * destinations.reduce((acc, d) => parseInt(d.percentage) + acc, 0) / 100.0
          }
        }
        if (transaction.destinations.length == 0) return total
        return 0
      })
      .reduce((acc, cost) => acc + cost, 0)
  }

  const totalPriceByDestination = (destinationId = false, swag = null) => {
    return filteredTransactions
      .map(transaction => {
        const total = transactionTotal(transaction, 'price', swag)
        if (destinationId) {
          const destination = transaction.destinations.find((d) => d.record_id == destinationId)
          if (destination) {
            return total * parseInt(destination.percentage) / 100.0
          }
          return 0
        }
        if (transaction.destinations.length == 0) return total
        return 0
      })
      .reduce((acc, price) => acc + price, 0)
  }

  const totalCostByDestination = (destinationId = false, swag = null) => {
    return filteredTransactions
      .map(transaction => {
        const total = transactionTotal(transaction, 'cost', swag)
        if (destinationId) {
          const destination = transaction.destinations.find((d) => d.record_id == destinationId)
          if (destination) {
            return total * parseInt(destination.percentage) / 100.0
          }
          return 0
        }
        if (transaction.destinations.length == 0) return total
        return 0
      })
      .reduce((acc, cost) => acc + cost, 0)
  }

  const totalPriceByRegion = (regionId = false, swag = null) => {
    return filteredTransactions
      .filter(transaction => {
        if (regionId) return transaction.region_id == regionId
        return transaction.region_id == null
      })
      .reduce((acc, transaction) => acc + transactionTotal(transaction, 'price', swag), 0)
  }

  const totalCostByRegion = (regionId = false, swag = null) => {
    return filteredTransactions
      .filter(transaction => { 
        if (regionId) return transaction.region_id == regionId
        return transaction.region_id == null
      })
      .reduce((acc, transaction) => acc + transactionTotal(transaction, 'cost', swag), 0)
  }

  const totalPriceByCategory = (category = false, swag = null) => {
    return filteredTransactions
      .filter(transaction => {
        if (transaction.category == null && category == '') return true
        if (category) return transaction.category == category
      })
      .reduce((acc, transaction) => acc + transactionTotal(transaction, 'price', swag), 0)
  }

  const totalCostByCategory = (category = false, swag = null) => {
    return filteredTransactions
      .filter(transaction => { 
        if (transaction.category == null && category == '') return true
        if (category) return transaction.category == category
      })
      .reduce((acc, transaction) => acc + transactionTotal(transaction, 'cost', swag), 0)
  }

  const updateCategories = (value, e) => {
    setCategories(categories.map(category => {
      if (category.value == value) {
        return { ...category, selected: e.target.checked }
      } else {
        return category
      }
    }))
  }

  // probably remove this some time in the future
  // const filteredTransactionsNoYears = useMemo(
  //   () => {
  //     if (!report.transactions?.length) return
  //     const selectedCategories = categories.filter(category => category.selected).map(category => category.value == '' ? null : category.value)
  //     const transactions = report.transactions.map(transaction => {
  //       return {
  //         ...transaction,
  //         destinations: transaction.destinations_transactions_attributes.map(dt => ({percentage: dt.percentage, ...destinations.find(destination => destination.record_id == dt.destination_id )})),
  //       }
  //     })
  //     if (selectedCategories.length == 0) return transactions
  //     return transactions.filter(transaction => {
  //       return selectedCategories.indexOf(transaction.category) != -1
  //     })
  //   },
  //   [report.transactions, categories, destinations]
  // )
  const filteredTransactions = useMemo(
    () => {
      if (!report.transactions?.length) return
      const selectedCategories = categories.filter(category => category.selected).map(category => category.value == '' ? null : category.value)
      const transactions = report.transactions.map(transaction => {
        return {
          ...transaction,
          destinations: transaction.destinations_transactions_attributes.map(dt => {
            const destination = destinations.find(destination => destination.record_id == dt.destination_id )
            const r = {percentage: dt.percentage, ...destination}
            if (transaction.ops_year) {
              r.financeCategory = `${transaction.ops_year} - ${r.financeCategory}`
            }
            return r
          }),
        }
      })
      if (selectedCategories.length == 0) return transactions
      return transactions.filter(transaction => {
        return selectedCategories.indexOf(transaction.category) != -1
      })
    },
    [report.transactions, categories, destinations]
  )

  // probably remove this some time in the future
  // const financeCategoriesNoYear = useMemo(
  //   () => {
  //     if (!destinations?.length) return []
  //     const unique = new Set()
  //     return destinations
  //       .filter(destination => destination.financeCategory && !unique.has(destination.financeCategory) && unique.add(destination.financeCategory))
  //       .map(destination => [destination.financeCategory, destination.tourcode])
  //       .sort((a, b) => a[0] > b[0] ? 1 : -1)
  //   },
  //   [destinations]
  // )
  const financeCategories = useMemo(
    () => {
      if (!filteredTransactions?.length) return []
      if (!destinations?.length) return []
      const unique = new Set()
      return filteredTransactions
        .flatMap(t => t.destinations)
        .filter(destination => destination.financeCategory && !unique.has(destination.financeCategory) && unique.add(destination.financeCategory))
        .map(destination => [destination.financeCategory, destination.tourcode])
        .sort((a, b) => a[0] > b[0] ? 1 : -1)
    },
    [filteredTransactions]
  )

  const region = (regionId) => {
    if (!report.regions?.length) return ''
    return report.regions.find(region => region.record_id == regionId)?.name
  }

  if (loading || loadingDestinations || loadingRegions) return <Loading />

  return (
    <ReportContext.Provider value={report}>
      <div className="flex justify-between gap-4 mb-4 items-end">
        <div className='flex gap-4'>
          <h1 className="text-4xl">Categories</h1>
        </div>
      </div>
      <div className="mb-12 flex gap-4">
        <div className="flex gap-2 flex-wrap mb-4">
          {categories.map(category =>
            <label key={category.value} className="flex gap-2 p-2 bg-gray-100 rounded">
              <input type="checkbox" onChange={(e) => updateCategories(category.value, e)} checked={category.selected ?? false} />
              {category.label == '- select -' ? 'Uncategorized' : category.label}
            </label>
          )}
        </div>
        <div className="flex">
          <div>
            <Button
              onClick={() => setCategories(categories.map(category => ({ ...category, selected: true })))}
              className="rounded-r-none border-white border-r"
            >
              All
            </Button>
          </div>
          <div>
            <Button
              onClick={() => setCategories(categories.map(category => ({ ...category, selected: false })))}
              className="rounded-l-none"
            >
              None
            </Button>
          </div>
        </div>
      </div>
      <div className="mb-12">
        <ExcelTable title="Totals by Category">
          <Table
            className="mb-8"
            data-f-name="Calibri"
            data-f-sz="16"
            data-frozen-rows="2"
            data-cols-width="
              25,
              15,
              15,
              15,
              15,
              15,
              15,
            "
          >
            <Table.Head>
              <Table.Row>
                <Table.Th rowSpan="2" data-fill-color="e8e8e8">Category</Table.Th>
                <Table.Th colSpan="2" data-fill-color="e8e8e8">Swag</Table.Th>
                <Table.Th colSpan="2" data-fill-color="e8e8e8">Not-Swag</Table.Th>
                <Table.Th colSpan="2" data-fill-color="e8e8e8">Total</Table.Th>
              </Table.Row>
              <Table.Row>
                <Table.Th data-fill-color="e8e8e8">Price</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Cost</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Price</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Cost</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Price</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Cost</Table.Th>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {categories.filter((c) => c.selected).map(category => (
                <Table.Row key={category.value}>
                  <Table.Td>{category.label == '- select -' ? 'Uncategorized' : category.label}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByCategory(category.value, true)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByCategory(category.value, true)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByCategory(category.value, false)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByCategory(category.value, false)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByCategory(category.value, null)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByCategory(category.value, null)).format('$0,0.00')}</Table.Td>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </ExcelTable>
      </div>
      <div className="mb-12">
        <ExcelTable title="Totals by Region">
          <Table
            className="mb-8"
            data-f-name="Calibri"
            data-f-sz="16"
            data-frozen-rows="1"
            data-cols-width="
              25,
              15,
              15,
            "
          >
            <Table.Head>
              <Table.Row>
                <Table.Th rowSpan="2" data-fill-color="e8e8e8">Region</Table.Th>
                <Table.Th colSpan="2" data-fill-color="e8e8e8">Swag</Table.Th>
                <Table.Th colSpan="2" data-fill-color="e8e8e8">Not-Swag</Table.Th>
                <Table.Th colSpan="2" data-fill-color="e8e8e8">Total</Table.Th>
              </Table.Row>
              <Table.Row>
                <Table.Th data-fill-color="e8e8e8">Price</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Cost</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Price</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Cost</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Total Price</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Total Cost</Table.Th>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <Table.Row>
                <Table.Td>No Region</Table.Td>
                <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByRegion(null, true)).format('$0,0.00')}</Table.Td>
                <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByRegion(null, true)).format('$0,0.00')}</Table.Td>
                <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByRegion(null, false)).format('$0,0.00')}</Table.Td>
                <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByRegion(null, false)).format('$0,0.00')}</Table.Td>
                <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByRegion()).format('$0,0.00')}</Table.Td>
                <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByRegion()).format('$0,0.00')}</Table.Td>
              </Table.Row>
              {regions?.map(region => (
                <Table.Row key={region.record_id}>
                  <Table.Td>{region.name}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByRegion(region.record_id, true)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByRegion(region.record_id, true)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByRegion(region.record_id, false)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByRegion(region.record_id, false)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByRegion(region.record_id)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByRegion(region.record_id)).format('$0,0.00')}</Table.Td>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </ExcelTable>
      </div>
      <div className="mb-12">
        <ExcelTable title="Totals by Finance Category">
          <Table
            className="mb-8"
            data-f-name="Calibri"
            data-f-sz="16"
            data-frozen-rows="1"
            data-cols-width="
              35,
              15,
              15,
              15,
            "
          >
            <Table.Head>
              <Table.Row>
                <Table.Th rowSpan="2" data-fill-color="e8e8e8">Finance Category</Table.Th>
                <Table.Th rowSpan="2" data-fill-color="e8e8e8">Tourcode</Table.Th>
                <Table.Th colSpan="2" data-fill-color="e8e8e8">Swag</Table.Th>
                <Table.Th colSpan="2" data-fill-color="e8e8e8">Not-Swag</Table.Th>
                <Table.Th colSpan="2" data-fill-color="e8e8e8">Total</Table.Th>
              </Table.Row>
              <Table.Row>
                <Table.Th data-fill-color="e8e8e8">Total Price</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Total Cost</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Total Price</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Total Cost</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Total Price</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Total Cost</Table.Th>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <Table.Row>
                <Table.Td>No Category</Table.Td>
                <Table.Td>No Tourcode</Table.Td>
                <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByFinanceCategory(null, true)).format('$0,0.00')}</Table.Td>
                <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByFinanceCategory(null, true)).format('$0,0.00')}</Table.Td>
                <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByFinanceCategory(null, false)).format('$0,0.00')}</Table.Td>
                <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByFinanceCategory(null, false)).format('$0,0.00')}</Table.Td>
                <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByFinanceCategory()).format('$0,0.00')}</Table.Td>
                <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByFinanceCategory()).format('$0,0.00')}</Table.Td>
              </Table.Row>
              {financeCategories.map(([category, tourcode]) => (
                <Table.Row key={tourcode}>
                  <Table.Td>{category}</Table.Td>
                  <Table.Td>{tourcode}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByFinanceCategory(category, true)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByFinanceCategory(category, true)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByFinanceCategory(category, false)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByFinanceCategory(category, false)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByFinanceCategory(category)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByFinanceCategory(category)).format('$0,0.00')}</Table.Td>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </ExcelTable>
      </div>
      <div className="mb-12">
        <ExcelTable title="Totals by Destination">
          <Table
            className="mb-8"
            data-f-name="Calibri"
            data-f-sz="16"
            data-frozen-rows="1"
            data-cols-width="
              45,
              15,
              15,
            "
          >
            <Table.Head>
              <Table.Row>
                <Table.Th rowSpan="2" data-fill-color="e8e8e8">Destination</Table.Th>
                <Table.Th colSpan="2" data-fill-color="e8e8e8">Swag</Table.Th>
                <Table.Th colSpan="2" data-fill-color="e8e8e8">Not-Swag</Table.Th>
                <Table.Th colSpan="2" data-fill-color="e8e8e8">Total</Table.Th>
              </Table.Row>
              <Table.Row>
                <Table.Th data-fill-color="e8e8e8">Total Price</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Total Cost</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Total Price</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Total Cost</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Total Price</Table.Th>
                <Table.Th data-fill-color="e8e8e8">Total Cost</Table.Th>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <Table.Row>
                <Table.Td>No Destination</Table.Td>
                <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByDestination(null, true)).format('$0,0.00')}</Table.Td>
                <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByDestination(null, true)).format('$0,0.00')}</Table.Td>
                <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByDestination(null, false)).format('$0,0.00')}</Table.Td>
                <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByDestination(null, false)).format('$0,0.00')}</Table.Td>
                <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByDestination()).format('$0,0.00')}</Table.Td>
                <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByDestination()).format('$0,0.00')}</Table.Td>
              </Table.Row>
              {destinations?.map(destination => (
                <Table.Row key={destination.record_id}>
                  <Table.Td>{destination.name}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByDestination(destination.record_id, true)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByDestination(destination.record_id, true)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByDestination(destination.record_id, false)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByDestination(destination.record_id, false)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalPriceByDestination(destination.record_id)).format('$0,0.00')}</Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(totalCostByDestination(destination.record_id)).format('$0,0.00')}</Table.Td>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </ExcelTable>
      </div>
      <ExcelTable title="Transactions">
        <Table
          data-f-name="Calibri"
          data-f-sz="16"
          data-frozen-rows="1"
          data-cols-width="
            22,
            25,
            20,
            25,
            12,
            35,
            35,
            12,
            12,
            12,
            12,
            12,
            12
          "
        >
          <Table.Head>
            <Table.Row>
              <Table.Th data-fill-color="e8e8e8">Date</Table.Th>
              <Table.Th data-fill-color="e8e8e8">Category</Table.Th>
              <Table.Th data-fill-color="e8e8e8">Region</Table.Th>
              <Table.Th data-fill-color="e8e8e8">Destination</Table.Th>
              <Table.Th data-fill-color="e8e8e8">Product ID</Table.Th>
              <Table.Th data-fill-color="e8e8e8">Parent</Table.Th>
              <Table.Th data-fill-color="e8e8e8">Product</Table.Th>
              <Table.Th data-fill-color="e8e8e8">Swag</Table.Th>
              <Table.Th data-fill-color="e8e8e8">Quantity Ordered</Table.Th>
              <Table.Th data-fill-color="e8e8e8">Price</Table.Th>
              <Table.Th data-fill-color="e8e8e8">Cost</Table.Th>
              <Table.Th width="120" data-fill-color="e8e8e8">Total Count</Table.Th>
              <Table.Th width="120" data-fill-color="e8e8e8">Total Price</Table.Th>
              <Table.Th width="120" data-fill-color="e8e8e8">Total Cost</Table.Th>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {filteredTransactions.map(transaction => <React.Fragment key={transaction.id}>
              <Table.Row>
                <Table.Td rowSpan={transaction.items_attributes.length}>{transaction.created_at}</Table.Td>
                <Table.Td rowSpan={transaction.items_attributes.length}>{transaction.category}</Table.Td>
                <Table.Td rowSpan={transaction.items_attributes.length}>{region(transaction.region_id)}</Table.Td>
                <Table.Td rowSpan={transaction.items_attributes.length}>{transaction.destinations.map(d => d.name).join(', ')}</Table.Td>
                {transaction.items_attributes && <>
                  <Table.Td data-t="n">
                    {transaction.items_attributes[0].magento_id}
                  </Table.Td>
                  <Table.Td>
                    {transaction.items_attributes[0].parent_title}
                  </Table.Td>
                  <Table.Td>
                    {transaction.items_attributes[0].title}
                  </Table.Td>
                  <Table.Td>
                    {transaction.items_attributes[0].swag ? 'Yes' : 'No'}
                  </Table.Td>
                  <Table.Td data-t="n">
                    {transaction.items_attributes[0].quantity}
                  </Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">
                    {numeral(transaction.items_attributes[0].quantity * parseFloat(transaction.items_attributes[0].price)).format('$0,0.00')}
                  </Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">
                    {numeral(transaction.items_attributes[0].quantity * parseFloat(transaction.items_attributes[0].cost)).format('$0,0.00')}
                  </Table.Td>
                </>}
                <Table.Td rowSpan={transaction.items_attributes.length} className="text-right" data-t="n">{transaction.items_attributes.reduce((acc, i) => acc + i.quantity, 0)}</Table.Td>
                <Table.Td rowSpan={transaction.items_attributes.length} className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(transactionTotal(transaction, 'price')).format('$0,0.00')}</Table.Td>
                <Table.Td rowSpan={transaction.items_attributes.length} className="text-right" data-t="n" data-num-fmt="$#,##0.00">{numeral(transactionTotal(transaction, 'cost')).format('$0,0.00')}</Table.Td>
              </Table.Row>
              {transaction.items_attributes.slice(1).map((item) => <React.Fragment key={item.id}>
                <Table.Row>
                  <Table.Td data-t="n">
                    {item.magento_id}
                  </Table.Td>
                  <Table.Td>
                    {item.parent_title}
                  </Table.Td>
                  <Table.Td>
                    {item.title}
                  </Table.Td>
                  <Table.Td>
                    {item.swag ? 'Yes' : 'No'}
                  </Table.Td>
                  <Table.Td data-t="n">
                     {item.quantity}
                  </Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">
                    {numeral(item.quantity * parseFloat(item.price)).format('$0,0.00')}
                  </Table.Td>
                  <Table.Td className="text-right" data-t="n" data-num-fmt="$#,##0.00">
                    {numeral(item.quantity * parseFloat(item.cost)).format('$0,0.00')}
                  </Table.Td>
                </Table.Row>
              </React.Fragment>)}
            </React.Fragment>)}
          </Table.Body>
        </Table>
      </ExcelTable>
    </ReportContext.Provider>
  )
}
